// Shared category list — used by the AI parser (enum) and the UI (filters/pickers).
// Travel is deliberately absent: it describes *why* money was spent, not what on,
// so it lives in `scope` (see lib/scopes.ts). Fuel stays separate from Car —
// fuel is variable spend, Car is maintenance.
export const CATEGORIES = [
  "Groceries",
  "Dining",
  "Cafe",
  "Transport",
  "Fuel",
  "Shopping",
  "Health",
  "Bills",
  "Subscriptions",
  "Entertainment",
  "Lodging",
  "Car",
  "Other",
] as const;

export type Category = (typeof CATEGORIES)[number];

// Emoji per category for compact mobile UI.
export const CATEGORY_EMOJI: Record<Category, string> = {
  Groceries: "🛒",
  Dining: "🍽️",
  Cafe: "☕",
  Transport: "🚗",
  Fuel: "⛽",
  Shopping: "🛍️",
  Health: "💊",
  Bills: "🧾",
  Subscriptions: "🔁",
  Entertainment: "🎬",
  Lodging: "🏨",
  Car: "🔧",
  Other: "📦",
};

// Per-category colour set for the light UI: a soft tinted avatar background,
// a saturated foreground for the emoji/letter, and a solid bar/dot colour for
// charts and category breakdowns. Classes are static so Tailwind keeps them.
export const CATEGORY_COLOR: Record<
  Category,
  { tint: string; text: string; bar: string; hex: string }
> = {
  Groceries: { tint: "bg-emerald-100", text: "text-emerald-700", bar: "bg-emerald-500", hex: "#10b981" },
  Dining: { tint: "bg-orange-100", text: "text-orange-700", bar: "bg-orange-500", hex: "#f97316" },
  Cafe: { tint: "bg-amber-100", text: "text-amber-700", bar: "bg-amber-500", hex: "#f59e0b" },
  Transport: { tint: "bg-blue-100", text: "text-blue-700", bar: "bg-blue-500", hex: "#3b82f6" },
  Fuel: { tint: "bg-red-100", text: "text-red-700", bar: "bg-red-500", hex: "#ef4444" },
  Shopping: { tint: "bg-pink-100", text: "text-pink-700", bar: "bg-pink-500", hex: "#ec4899" },
  Health: { tint: "bg-rose-100", text: "text-rose-700", bar: "bg-rose-500", hex: "#f43f5e" },
  Bills: { tint: "bg-slate-100", text: "text-slate-700", bar: "bg-slate-500", hex: "#64748b" },
  Subscriptions: { tint: "bg-violet-100", text: "text-violet-700", bar: "bg-violet-500", hex: "#8b5cf6" },
  Entertainment: { tint: "bg-fuchsia-100", text: "text-fuchsia-700", bar: "bg-fuchsia-500", hex: "#d946ef" },
  Lodging: { tint: "bg-sky-100", text: "text-sky-700", bar: "bg-sky-500", hex: "#0ea5e9" },
  Car: { tint: "bg-teal-100", text: "text-teal-700", bar: "bg-teal-500", hex: "#14b8a6" },
  Other: { tint: "bg-zinc-100", text: "text-zinc-700", bar: "bg-zinc-500", hex: "#71717a" },
};

// Single-letter hotkey per category (desktop power-user mode). All unique.
export const CATEGORY_HOTKEY: Record<Category, string> = {
  Groceries: "g",
  Dining: "d",
  Cafe: "c",
  Transport: "t",
  Fuel: "f",
  Shopping: "s",
  Health: "h",
  Bills: "b",
  Subscriptions: "u",
  Entertainment: "e",
  Lodging: "l",
  Car: "k",
  Other: "o",
};

// Reverse lookup: hotkey -> category, for keydown handling.
export const HOTKEY_TO_CATEGORY: Record<string, Category> = Object.fromEntries(
  (Object.entries(CATEGORY_HOTKEY) as [Category, string][]).map(([c, k]) => [k, c]),
) as Record<string, Category>;
